/*
 * My Leads tab (PROD-9977). Loads /v2/sales_task_list/my_leads for the caller, or for
 * `opsUuid` in Team's Leads view, and refreshes on the server's interval while the tab is visible.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Banner, Btn } from '../ui';
import MyLeadsView, { PickerBar } from './MyLeadsView';
import { fetchMyLeads, leadsErrorReason, leadsErrorStatus } from './api';
import {
  DEFAULT_PERIOD,
  NOT_ON_ROSTER,
  POLL_FALLBACK_MS,
  isLeadsTimeout,
  leadsErrorMessage,
  nextPollDelay,
  shouldCatchUp
} from './my_leads';
import type { LeadsPeriod, MyLeadsData } from './types';

interface LoadError {
  status?: number;
  message: string;
  // 503 my_leads_timeout: transient, retried on the next poll.
  timeout: boolean;
}

function Notice({ children, testId }: { children: React.ReactNode; testId?: string }) {
  return (
    <div
      data-testid={testId}
      className="mx-[5%] my-5 rounded-[10px] border border-line-subtle bg-surface-card px-5 py-8 text-center text-[13px] text-content-secondary max-sm:mx-3"
    >
      {children}
    </div>
  );
}

// Shared with the sales task list while /me decides whether a leads tab exists.
export function LeadsLoading() {
  return (
    <Notice testId="leads-loading">
      <span aria-busy="true" className="text-xs text-content-muted">
        Loading leads…
      </span>
    </Notice>
  );
}

interface Props {
  // Team's Leads view: the rep to show. Unset = the caller.
  opsUuid?: string;
  // From /me: false skips the request and shows the roster message.
  onRoster?: boolean;
  // Team's Leads rep dropdown: in the page header, or in its own bar when there is no page to show.
  picker?: React.ReactNode;
}

function MyLeadsPage({ opsUuid, onRoster = true, picker }: Props) {
  const [period, setPeriod] = useState<LeadsPeriod>(DEFAULT_PERIOD);
  const [data, setData] = useState<MyLeadsData | null>(null);
  const [error, setError] = useState<LoadError | null>(null);
  const [loading, setLoading] = useState(false);
  // Client time the shown data arrived; the view's "ago" labels are relative to it.
  const [loadedAt, setLoadedAt] = useState(0);
  // Bumps after every load so the next poll is rescheduled from the latest response.
  const [loadCount, setLoadCount] = useState(0);
  const seqRef = useRef(0);
  const dueRef = useRef(false);
  const lastLoadRef = useRef(0);
  // 403/400 will not fix themselves; polling stops until the tab is reopened.
  const stopRef = useRef(false);
  const viewing = !!opsUuid;
  const skip = !viewing && !onRoster;

  const load = useCallback(() => {
    seqRef.current += 1;
    const seq = seqRef.current;
    dueRef.current = false;
    lastLoadRef.current = Date.now();
    setLoading(true);
    return fetchMyLeads(period, opsUuid)
      .then((d) => {
        if (seq !== seqRef.current) return;
        setData(d);
        setLoadedAt(Date.now());
        setError(null);
      })
      .catch((e: unknown) => {
        if (seq !== seqRef.current) return;
        const status = leadsErrorStatus(e);
        const reason = leadsErrorReason(e);
        if (status === 400 || status === 403 || status === 404) stopRef.current = true;
        setError({
          status,
          message: leadsErrorMessage(status, reason),
          timeout: isLeadsTimeout(status, reason)
        });
      })
      .finally(() => {
        if (seq !== seqRef.current) return;
        setLoading(false);
        setLoadCount((n) => n + 1);
      });
  }, [period, opsUuid]);

  // A new period reloads at once; the old numbers stay dimmed until it lands.
  useEffect(() => {
    if (skip) return undefined;
    stopRef.current = false;
    setError(null);
    load();
    return () => {
      seqRef.current += 1;
    };
  }, [load, skip]);

  const genAt = data?.generated_at;
  const nextAt = data?.next_refresh_at;
  const servedAt = data?.server_time;
  const failed = !!error;

  // One timer per response, on the server's remaining wait from arrival (immune to client clock skew);
  // a hidden tab defers to visibilitychange. Failures, incl. 503 timeouts, retry on the fallback.
  useEffect(() => {
    if (skip || loadCount === 0 || stopRef.current) return undefined;
    const delay = failed
      ? POLL_FALLBACK_MS
      : nextPollDelay(genAt, nextAt, loadedAt, Date.now(), servedAt);
    const id = setTimeout(() => {
      if (document.visibilityState === 'hidden') dueRef.current = true;
      else load();
    }, delay);
    return () => clearTimeout(id);
  }, [skip, loadCount, genAt, nextAt, servedAt, loadedAt, failed, load]);

  useEffect(() => {
    if (skip) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'hidden' || stopRef.current) return;
      const now = Date.now();
      if (
        shouldCatchUp(genAt, nextAt, loadedAt, lastLoadRef.current, now, dueRef.current, servedAt)
      )
        load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [skip, genAt, nextAt, servedAt, loadedAt, load]);

  const errorMessage = error?.message;
  const timedOut = !!error?.timeout;
  const banner = useMemo(() => {
    if (!errorMessage) return null;
    if (timedOut) {
      return (
        <Banner tone="warning" role="status" testId="leads-refresh-timeout">
          {errorMessage}
        </Banner>
      );
    }
    return (
      <Banner tone="error" role="alert" testId="leads-refresh-error">
        {errorMessage} Showing the last loaded numbers.
      </Banner>
    );
  }, [errorMessage, timedOut]);

  const withPicker = (node: React.ReactNode) =>
    picker ? (
      <>
        <PickerBar>{picker}</PickerBar>
        {node}
      </>
    ) : (
      node
    );
  if (skip) return <Notice testId="leads-not-on-roster">{NOT_ON_ROSTER}</Notice>;
  if (!data) {
    if (error) {
      return withPicker(
        <Notice testId="leads-error">
          <p className="m-0 mb-3">{error.message}</p>
          {stopRef.current ? null : (
            <Btn variant="outline" onClick={() => load()}>
              Try again
            </Btn>
          )}
        </Notice>
      );
    }
    return withPicker(<LeadsLoading />);
  }
  if (data.rep.roster_state !== 'active' || !data.rep.level) {
    return withPicker(
      <Notice testId="leads-not-on-roster">
        {viewing ? leadsErrorMessage(400, 'target_not_rep') : NOT_ON_ROSTER}
      </Notice>
    );
  }
  return (
    <MyLeadsView
      data={data}
      now={loadedAt}
      period={period}
      onPeriod={setPeriod}
      viewing={viewing}
      loading={loading}
      stale={data.period_days !== period}
      banner={banner}
      picker={picker}
    />
  );
}

export default MyLeadsPage;
